import { useAuth } from '@/contexts/AuthContext';
import { useUpload } from '@/hooks/use-upload';
import { ChevronLeft } from '@/icons/ChevronLeft';
import { PencilLine } from '@/icons/PencilLine';
import { X } from '@/icons/X';
import { generateAPIUrl } from '@/utils/api-url';
import { cn } from '@/utils/cn';
import { COMPLETION_MODELS, type CompletionModelKey } from '@/utils/llm-models';
import { fetch as expoFetch } from 'expo/fetch';
import { hc } from 'hono/client';
import { useCallback, useRef, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  type TextInput,
  View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { ApiChatIdType } from '../app/api/chat/[id]+api';
import type { ApiPersonaType } from '../app/api/persona/[...everything]+api';
import type { ApiUploadType } from '../app/api/upload/[...everything]+api';
import { ListItem, ListSection } from './list';
import { Form, FormItem, FormSection } from './nativewind-ui/form';
import { TextField } from './nativewind-ui/text-field';
import { PersonaAvatar } from './persona-avatar';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Text } from './ui/text';
import { Textarea } from './ui/textarea';
import { Lock } from '@/icons/Lock';

type ChatModalScreen =
  | 'basicInfo'
  | 'customPrompt'
  | 'model'
  | 'setInactive'
  | 'setActive'
  | 'delete';

type SourceScreen = 'persona-active-list' | 'persona-inactive-list' | 'chat';

type Persona = {
  id: string;
  avatarUrl?: string | null;
  name: string;
  otherParticipantName?: string | null;
  customPrompt?: string | null;
  model?: CompletionModelKey | null;
};

const uploadClient = hc<ApiUploadType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

const personaClient = hc<ApiPersonaType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

const chatIdClient = hc<ApiChatIdType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

async function uploadStart(
  token: string,
  {
    id,
    fileSize,
    contentType
  }: { id: string; fileSize: number; contentType: string }
) {
  const res = await uploadClient.api.upload.avatar.$post(
    { json: { personaId: id, fileSize, contentType } },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const data = await res.json();
  if (data.valid !== true) {
    throw new Error(
      typeof data.error === 'string' ? data.error : 'Failed to upload'
    );
  }

  return data;
}

export type UpdatePersonaCallback = (persona: {
  name?: string;
  otherParticipantName?: string;
  avatarUrl?: string;
  customPrompt?: string;
  model?: CompletionModelKey;
}) => void;

export type OnCloseCallback = (removeFromCurrentScreen?: boolean) => void;

type Changes = {
  name?: string;
  otherParticipantName?: string;
  customPrompt?: string;
  avatar?: string;
  model?: CompletionModelKey;
};

async function saveChanges(
  token: string,
  {
    id,
    ...changes
  }: {
    id: string;
  } & Changes
) {
  const changesWithNulls = {
    ...changes,
    avatar: changes.avatar === '' ? null : changes.avatar,
    customPrompt: changes.customPrompt === '' ? null : changes.customPrompt,
    otherParticipantName:
      changes.otherParticipantName === '' ? null : changes.otherParticipantName
  };

  const res = await personaClient.api.persona[':id'].$put(
    { json: changesWithNulls, param: { id } },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const data = await res.json();
  if (data.valid !== true) {
    throw new Error(
      typeof data.error === 'string' ? data.error : 'Failed to update'
    );
  }

  return data;
}

async function archivePersona(token: string, id: string) {
  const res = await personaClient.api.persona[':id'].archive.$put(
    { param: { id } },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const data = await res.json();
  if (data.valid !== true) {
    throw new Error(
      typeof data.error === 'string' ? data.error : 'Failed to archive'
    );
  }

  return data;
}

async function deletePersona(
  token: string,
  {
    id
  }: {
    id: string;
  }
) {
  const res = await personaClient.api.persona[':id'].$delete(
    { param: { id } },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const data = await res.json();
  if (data.valid !== true) {
    throw new Error(
      typeof data.error === 'string' ? data.error : 'Failed to delete'
    );
  }

  return data;
}

async function setDeletionStatus(token: string, id: string, deleted: boolean) {
  const res = await personaClient.api.persona[':id'].setDeletionStatus.$put(
    { json: { deleted }, param: { id } },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const data = await res.json();
  if (data.valid !== true) {
    throw new Error(
      typeof data.error === 'string' ? data.error : 'Failed to update'
    );
  }

  return data;
}

async function saveChatChanges(
  token: string,
  {
    id,
    model
  }: {
    id: string;
    model: CompletionModelKey;
  }
) {
  const res = await chatIdClient.api.chat[':id'].$put(
    { json: { model }, param: { id } },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const data = await res.json();
  if (data.valid !== true) {
    throw new Error(
      typeof data.error === 'string' ? data.error : 'Failed to update'
    );
  }

  return data;
}

export function PersonaModal({
  chatId,
  isVisible,
  onClose,
  persona,
  updatePersona,
  sourceScreen
}: {
  chatId?: string;
  isVisible: boolean;
  onClose: OnCloseCallback;
  persona: Persona;
  updatePersona: UpdatePersonaCallback;
  sourceScreen: SourceScreen;
}) {
  const [currentScreen, setCurrentScreen] = useState<ChatModalScreen | null>(
    null
  );
  const { authState } = useAuth();
  const [isSaving, setIsSaving] = useState<'persona' | 'chat' | false>(false);

  const save = useCallback(
    async (changes: Changes) => {
      setIsSaving('persona');
      try {
        const result = await saveChanges(authState.token!, {
          id: persona.id,
          ...changes
        });

        if (result.avatarUrl) {
          updatePersona({ ...changes, avatarUrl: result.avatarUrl });
        } else {
          updatePersona(changes);
        }
        setCurrentScreen(null);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to save');
      } finally {
        setIsSaving(false);
      }
    },
    [authState.token]
  );

  const saveChatModel = useCallback(
    async ({ model }: { model: CompletionModelKey }) => {
      setIsSaving('chat');
      try {
        await saveChatChanges(authState.token!, {
          id: chatId!,
          model
        });

        updatePersona({ model });
        setCurrentScreen(null);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to save');
      } finally {
        setIsSaving(false);
      }
    },
    [authState.token]
  );

  const saveModel = useCallback(
    (saveTo: 'persona' | 'chat', model: CompletionModelKey) => {
      if (saveTo === 'persona') {
        return save({ model });
      }
      return saveChatModel({ model });
    },
    [save, saveChatModel]
  );

  let ScreenComponent;
  switch (currentScreen) {
    case 'basicInfo':
      ScreenComponent = BasicInfo;
      break;
    case 'customPrompt':
      ScreenComponent = CustomPrompt;
      break;
    case 'model':
      ScreenComponent = Model;
      break;
    case 'setActive':
      ScreenComponent = SetActive;
      break;
    case 'setInactive':
      ScreenComponent = SetInactive;
      break;
    case 'delete':
      ScreenComponent = ConfirmDelete;
      break;
    default:
      ScreenComponent = SelectScreen;
  }

  return (
    <Modal animationType="slide" transparent visible={isVisible}>
      <View
        className={cn(
          'absolute bottom-0 w-full rounded-t-[18px] bg-[#25292e]',
          currentScreen ? 'h-[70%]' : 'h-[70%]'
        )}
      >
        <View className="h-20 flex-row items-center justify-between rounded-t-[10px] bg-[#464C55] px-5">
          {currentScreen && (
            <ScreenBackButton onPress={() => setCurrentScreen(null)} />
          )}
          <View className="flex flex-row items-center">
            {currentScreen === 'basicInfo' &&
            sourceScreen !== 'persona-inactive-list' ? (
              <PersonaAvatarUpload persona={persona} save={save} />
            ) : (
              <PersonaAvatar persona={persona} />
            )}
            <Text className="pl-4">{persona.name}</Text>
          </View>
          <Pressable
            onPress={() => {
              setCurrentScreen(null);
              onClose();
            }}
          >
            <X color="#fff" size={22} />
          </Pressable>
        </View>

        <ScreenComponent
          setCurrentScreen={setCurrentScreen}
          persona={persona}
          save={save}
          saveModel={saveModel}
          isSaving={isSaving}
          hasChatId={chatId !== undefined}
          sourceScreen={sourceScreen}
          onClose={onClose}
        />
      </View>
    </Modal>
  );
}

type ScreenProps = {
  setCurrentScreen: (screen: ChatModalScreen | null) => void;
  persona: Persona;
  save: (changes: Changes) => Promise<void>;
  saveModel: (
    saveTo: 'persona' | 'chat',
    model: CompletionModelKey
  ) => Promise<void>;
  isSaving: 'persona' | 'chat' | false;
  hasChatId: boolean;
  sourceScreen: SourceScreen;
  onClose: OnCloseCallback;
};

function ScreenBackButton({ onPress }: { onPress: () => void }) {
  return (
    <Pressable className="pt-2" onPress={onPress}>
      <ChevronLeft color="#fff" size={22} />
    </Pressable>
  );
}

function BasicInfo({ persona, isSaving, save, sourceScreen }: ScreenProps) {
  const readOnly = sourceScreenIsReadOnly(sourceScreen);
  const [name, setName] = useState(persona.name);
  const [otherParticipant, setOtherParticipant] = useState(
    persona.otherParticipantName || ''
  );
  const otherParticipantRef = useRef<TextInput>(null);

  const saveBasicInfo = () =>
    save({
      name,
      otherParticipantName: otherParticipant
    });

  return (
    <SafeAreaView style={styles.keyboardAvoidingViewStyles}>
      <KeyboardAvoidingView
        behavior="padding"
        style={styles.keyboardAvoidingViewStyles}
      >
        <ScrollView className="flex-1">
          <Form className="gap-5 px-4 pt-8">
            <FormSection>
              <FormItem>
                <TextField
                  autoFocus
                  label={Platform.select({ ios: undefined, default: 'Name' })}
                  leftView={Platform.select({
                    ios: <LeftLabel isWide>Name</LeftLabel>
                  })}
                  placeholder="required"
                  value={name}
                  onChangeText={setName}
                  onSubmitEditing={() => otherParticipantRef.current?.focus()}
                  submitBehavior="submit"
                  enterKeyHint="next"
                  editable={!readOnly}
                />
              </FormItem>
              <FormItem>
                <TextField
                  ref={otherParticipantRef}
                  label={Platform.select({
                    ios: undefined,
                    default: 'Other Participant'
                  })}
                  leftView={Platform.select({
                    ios: <LeftLabel isWide>Other Participant</LeftLabel>
                  })}
                  placeholder="optional"
                  value={otherParticipant}
                  onChangeText={setOtherParticipant}
                  onSubmitEditing={saveBasicInfo}
                  submitBehavior="submit"
                  enterKeyHint="done"
                  editable={!readOnly}
                />
              </FormItem>
            </FormSection>

            {!readOnly && (
              <Button
                className="rounded-full"
                onPress={saveBasicInfo}
                disabled={!!isSaving}
              >
                {isSaving ? (
                  <View className="py-1">
                    <ActivityIndicator color="black" />
                  </View>
                ) : (
                  <Text>Save</Text>
                )}
              </Button>
            )}
          </Form>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

function CustomPrompt({ persona, isSaving, save, sourceScreen }: ScreenProps) {
  const readOnly = sourceScreenIsReadOnly(sourceScreen);
  const [prompt, setPrompt] = useState(persona.customPrompt || '');

  const savePrompt = () =>
    save({
      customPrompt: prompt
    });

  const characterLimit = 1024;

  return (
    <SafeAreaView style={styles.keyboardAvoidingViewStyles}>
      <KeyboardAvoidingView
        behavior="padding"
        style={styles.keyboardAvoidingViewStyles}
      >
        <ScrollView className="flex-1">
          <Form className="gap-5 px-4 pt-4">
            <Label nativeID="prompt">
              Do you want to add any personal preferences? Any chat content to
              focus on or avoid?
            </Label>
            <View>
              <Textarea
                aria-labelledby="prompt"
                onSubmitEditing={savePrompt}
                autoFocus
                value={prompt}
                onChangeText={(value) => {
                  if (!readOnly && value.length <= characterLimit) {
                    setPrompt(value);
                  }
                }}
                returnKeyType="done"
                maxLength={characterLimit}
                editable={!readOnly}
              />
              <View className="flex flex-row-reverse">
                <Text className="text-right text-sm text-gray-500 dark:text-gray-400">
                  {prompt.length}/{characterLimit}
                </Text>
              </View>
            </View>

            {!readOnly && (
              <Button
                className="rounded-full"
                onPress={savePrompt}
                disabled={!!isSaving}
              >
                {isSaving ? (
                  <View className="py-1">
                    <ActivityIndicator color="black" />
                  </View>
                ) : (
                  <Text>Save</Text>
                )}
              </Button>
            )}
          </Form>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

function Model({
  isSaving,
  persona,
  saveModel,
  hasChatId,
  sourceScreen
}: ScreenProps) {
  const readOnly = sourceScreenIsReadOnly(sourceScreen);
  const { user } = useAuth();
  const [model, setModel] = useState<CompletionModelKey>(
    persona.model || COMPLETION_MODELS[0].key
  );
  const [isConfirming, setIsConfirming] = useState(false);

  function onLabelPress(label: CompletionModelKey) {
    return () => {
      if (!readOnly) {
        setModel(label);
      }
    };
  }

  const confirmModel = useCallback(() => {
    setIsConfirming(true);
  }, []);

  const saveModelCallback = useCallback(
    (saveTo: 'persona' | 'chat') => {
      saveModel(saveTo, model);
    },
    [model]
  );

  return (
    <SafeAreaView
      edges={['left', 'right', 'bottom']}
      style={styles.keyboardAvoidingViewStyles}
    >
      <ScrollView className="flex-1">
        <Form className="gap-5 px-4 pt-4">
          {isConfirming && !readOnly ? (
            <View>
              <Label>
                Do you want to use the{' '}
                {COMPLETION_MODELS.find((m) => m.key === model)?.label || ''}{' '}
                model to all future conversations with {persona.name} or just
                this one?
              </Label>

              <Button
                onPress={() => saveModelCallback('persona')}
                className="mt-4 rounded-full"
                disabled={isSaving === 'persona'}
              >
                {isSaving === 'persona' ? (
                  <ActivityIndicator size="small" color="black" />
                ) : (
                  <Text>Apply to all future conversations</Text>
                )}
              </Button>
              <Button
                onPress={() => saveModelCallback('chat')}
                className="mt-4 rounded-full"
                disabled={isSaving === 'chat'}
              >
                {isSaving === 'chat' ? (
                  <ActivityIndicator size="small" color="black" />
                ) : (
                  <Text>Apply to this conversation only</Text>
                )}
              </Button>
            </View>
          ) : (
            <View>
              {user?.planKeyname === 'free' ? (
                <View className="flex flex-row">
                  <Lock size={22} className="mr-2 text-white" />
                  <Text className="mb-4 font-semibold web:font-medium">
                    Limited Preview: Upgrade for continued access
                  </Text>
                </View>
              ) : null}
              <Label nativeID="prompt">
                {readOnly
                  ? 'Current model configuration:'
                  : 'Experiment with different models to match your preferences.'}
              </Label>

              <View className="my-4 mr-4 flex-1">
                <RadioGroup
                  value={model}
                  onValueChange={(value: string) => {
                    if (!readOnly) {
                      setModel(value as CompletionModelKey);
                    }
                  }}
                  className="gap-3"
                >
                  {COMPLETION_MODELS.map((model) => (
                    <RadioGroupItemWithLabel
                      key={model.key}
                      value={model.key}
                      label={model.label}
                      description={model.description}
                      onLabelPress={onLabelPress(model.key)}
                    />
                  ))}
                </RadioGroup>
              </View>

              {!readOnly && (
                <Button
                  className="rounded-full"
                  onPress={() => {
                    if (hasChatId) {
                      confirmModel();
                    } else {
                      saveModel('persona', model);
                    }
                  }}
                  disabled={!!isSaving}
                >
                  {isSaving ? (
                    <View className="py-1">
                      <ActivityIndicator color="black" />
                    </View>
                  ) : (
                    <Text>Save</Text>
                  )}
                </Button>
              )}
            </View>
          )}
        </Form>
      </ScrollView>
    </SafeAreaView>
  );
}

function RadioGroupItemWithLabel({
  value,
  label,
  description,
  onLabelPress
}: {
  value: string;
  label: string;
  description: string;
  onLabelPress: () => void;
}) {
  return (
    <View className="flex-row items-center gap-2">
      <RadioGroupItem aria-labelledby={`label-for-${value}`} value={value} />
      <Pressable onPress={onLabelPress}>
        <Label nativeID={`label-for-${value}`}>{label}</Label>

        <Text className="text-gray-400">{description}</Text>
      </Pressable>
    </View>
  );
}

function SetActive({ persona, setCurrentScreen, onClose }: ScreenProps) {
  const { authState } = useAuth();
  const [isConfirming, setIsConfirming] = useState(false);

  const onConfirm = useCallback(async () => {
    setIsConfirming(true);
    try {
      await setDeletionStatus(authState.token!, persona.id, false);
      setCurrentScreen(null);
      onClose(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to restore persona');
    } finally {
      setIsConfirming(false);
    }
  }, [authState.token, persona.id]);

  const onCancel = useCallback(() => {
    setCurrentScreen(null);
  }, []);

  return (
    <SafeAreaView
      edges={['left', 'right', 'bottom']}
      style={styles.keyboardAvoidingViewStyles}
    >
      <ScrollView className="flex-1">
        <Form className="gap-5 px-4 pt-4">
          <View>
            <Label>Are you sure you want to restore this Persona?</Label>
            <Button
              className="mb-3 mt-4 rounded-full"
              onPress={onConfirm}
              disabled={isConfirming}
            >
              {isConfirming ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <Text>Restore Persona</Text>
              )}
            </Button>
            <Button
              variant="outline"
              className="rounded-full"
              onPress={onCancel}
            >
              <Text>Cancel</Text>
            </Button>
          </View>
        </Form>
      </ScrollView>
    </SafeAreaView>
  );
}

function SetInactive({ persona, setCurrentScreen, onClose }: ScreenProps) {
  const { authState } = useAuth();
  const [isConfirming, setIsConfirming] = useState(false);
  const [showInternalArchive, setShowInternalArchive] = useState(false);

  const onConfirm = useCallback(async () => {
    setIsConfirming(true);
    try {
      await setDeletionStatus(authState.token!, persona.id, true);
      setCurrentScreen(null);
      onClose(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to archive');
    } finally {
      setIsConfirming(false);
    }
  }, [authState.token, persona.id]);

  const onInternalArchive = useCallback(async () => {
    setIsConfirming(true);
    try {
      await archivePersona(authState.token!, persona.id);
      setCurrentScreen(null);
      onClose();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to archive');
    } finally {
      setIsConfirming(false);
    }
  }, [authState.token, persona.id]);

  const onCancel = useCallback(() => {
    setCurrentScreen(null);
  }, []);

  return (
    <SafeAreaView
      edges={['left', 'right', 'bottom']}
      style={styles.keyboardAvoidingViewStyles}
    >
      <ScrollView className="flex-1">
        <Form className="gap-5 px-4 pt-4">
          <View>
            <Label>
              Are you sure you want to archive this Persona? It will be moved to
              your "archived" tab of this screen.
            </Label>
            <Button
              variant="destructive"
              className="mb-3 mt-4 rounded-full"
              onPress={onConfirm}
              onLongPress={() => setShowInternalArchive(true)}
              disabled={isConfirming}
            >
              {isConfirming ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <Text>Archive</Text>
              )}
            </Button>

            <Button className="rounded-full" onPress={onCancel}>
              <Text>Cancel</Text>
            </Button>
            {showInternalArchive && (
              <Button
                variant="destructive"
                className="mb-3 mt-3 rounded-full"
                onPress={onInternalArchive}
                disabled={isConfirming}
              >
                {isConfirming ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <Text>Archive (Internal)</Text>
                )}
              </Button>
            )}
          </View>
        </Form>
      </ScrollView>
    </SafeAreaView>
  );
}

function ConfirmDelete({ persona, setCurrentScreen, onClose }: ScreenProps) {
  const { authState } = useAuth();
  const [isConfirming, setIsConfirming] = useState(false);

  const onConfirm = useCallback(async () => {
    setIsConfirming(true);
    try {
      await deletePersona(authState.token!, { id: persona.id });
      setCurrentScreen(null);
      onClose(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setIsConfirming(false);
    }
  }, [authState.token, persona.id]);

  const onCancel = useCallback(() => {
    setCurrentScreen(null);
  }, []);

  return (
    <SafeAreaView
      edges={['left', 'right', 'bottom']}
      style={styles.keyboardAvoidingViewStyles}
    >
      <ScrollView className="flex-1">
        <Form className="gap-5 px-4 pt-4">
          <View>
            <Label>
              Are you sure you want to permanently delete this Persona? This
              action cannot be undone.
            </Label>
            <Button
              variant="destructive"
              className="mb-3 mt-4 rounded-full"
              onPress={onConfirm}
              disabled={isConfirming}
            >
              {isConfirming ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <Text>Delete Permanently</Text>
              )}
            </Button>
            <Button className="rounded-full" onPress={onCancel}>
              <Text>Cancel</Text>
            </Button>
          </View>
        </Form>
      </ScrollView>
    </SafeAreaView>
  );
}

function SelectScreen({
  setCurrentScreen,
  persona,
  sourceScreen
}: ScreenProps) {
  const { user } = useAuth();
  return (
    <ScrollView className="flex-1">
      <View className="mx-2 my-4">
        <ListSection title="Basic Info">
          <ListItem
            title="Name"
            value={persona.name}
            isFirst
            onPress={() => setCurrentScreen('basicInfo')}
          />
          <ListItem
            title="Other Participant"
            value={persona.otherParticipantName}
            isLast
            onPress={() => setCurrentScreen('basicInfo')}
          />
        </ListSection>
        <ListSection title="Chat Preferences">
          <ListItem
            title="Preferences"
            value={persona.customPrompt || 'Add your preferences'}
            isFirst
            isLast
            onPress={() => setCurrentScreen('customPrompt')}
          />
        </ListSection>
        <ListSection title="Model Selection">
          <ListItem
            title="Model"
            modifier={user?.planKeyname === 'free' ? 'lock' : undefined}
            value={
              user?.planKeyname === 'free'
                ? 'Upgrade to Access'
                : COMPLETION_MODELS.find((m) => m.key === persona.model)
                    ?.label || 'Default'
            }
            isFirst
            onPress={() => setCurrentScreen('model')}
            isLast
          />
        </ListSection>

        {sourceScreen === 'persona-active-list' && (
          <ListSection title="Danger Zone" modifier="danger">
            <ListItem
              title="Archive"
              value={`Archive ${persona.name}`}
              isFirst
              onPress={() => setCurrentScreen('setInactive')}
              isLast
            />
          </ListSection>
        )}

        {sourceScreen === 'persona-inactive-list' && (
          <ListSection title="Danger Zone" modifier="danger">
            <ListItem
              title="Restore"
              value={`Restore ${persona.name}`}
              isFirst
              onPress={() => setCurrentScreen('setActive')}
            />
            <ListItem
              title="Delete"
              value={`Delete ${persona.name}`}
              onPress={() => setCurrentScreen('delete')}
              isLast
              modifier="danger"
            />
          </ListSection>
        )}
      </View>
    </ScrollView>
  );
}

function PersonaAvatarUpload({
  persona,
  save
}: {
  persona: Persona;
  save: (changes: Changes) => void;
}) {
  const { authState, user } = useAuth();
  const [uploadStatus, setUploadStatus] = useState<
    'idle' | 'uploading' | 'done'
  >('idle');
  const userId = user?.id;
  const { getAssetInfo, startUpload, pickFile } = useUpload();

  const pickImage = useCallback(async () => {
    const fileUri = await pickFile('image');

    if (!fileUri) {
      return;
    }
    const fileInfo = await getAssetInfo(fileUri);

    if (fileInfo.size > 20 * 1024 * 1024 /* 20 MB */) {
      alert('File size too large, please upload a file under 20MB.');
      return;
    }

    if (!authState.token || !userId) {
      alert('You must be logged in to upload an avatar.');
      return;
    }

    if (!fileInfo.type.startsWith('image/')) {
      alert('Invalid file type. Please upload an image file.');
      return;
    }

    setUploadStatus('uploading');

    try {
      const uploadStartResult = await uploadStart(authState.token, {
        id: persona.id,
        fileSize: fileInfo.size,
        contentType: fileInfo.type
      });

      startUpload({
        fileUri,
        mimeType: fileInfo.type,
        presignedUrl: uploadStartResult.presignedUrl,
        onSuccess: () => {
          save({ avatar: uploadStartResult.uploadId });
          setUploadStatus('done');
        },
        onError: (error) => {
          console.error(error);
          setUploadStatus('idle');
          alert('Failed to upload avatar');
        }
      });
    } catch (error: any) {
      console.log(error);
      setUploadStatus('idle');
      alert(error.toString() || 'An unknown error occurred');
      return;
    }
  }, [authState.token, getAssetInfo, startUpload, pickFile, userId]);

  return (
    <View className="relative">
      <PersonaAvatar persona={persona} />
      <Pressable
        onPress={pickImage}
        className="absolute left-0 top-0 flex h-12 w-12 items-center justify-center rounded-full align-middle"
      >
        {uploadStatus === 'uploading' ? (
          <ActivityIndicator
            size="small"
            color="white"
            style={styles.avatarUploading}
          />
        ) : (
          <PencilLine
            className="h-full w-full stroke-white stroke-2 web:h-[50%] web:w-[50%]"
            size={28}
          />
        )}
      </Pressable>
    </View>
  );
}

function LeftLabel({
  children,
  isWide
}: {
  children: string;
  isWide?: boolean;
}) {
  return (
    <View className={cn('justify-center pl-2', isWide ? 'w-32' : 'w-28')}>
      <Text className="font-medium">{children}</Text>
    </View>
  );
}

function sourceScreenIsReadOnly(sourceScreen: SourceScreen) {
  return sourceScreen === 'persona-inactive-list';
}

const styles = StyleSheet.create({
  keyboardAvoidingViewStyles: {
    flex: 1
  },
  avatarUploading: {
    width: '100%',
    height: '100%'
  }
});
